import { useSyncExternalStore } from 'react';
import { Plus, Pin, Trash2, ArrowUpDown } from '../../icons';
import { tMemo, getMemoLang, subscribeMemoLang } from '../../lib/memo-i18n';
import { formatDate } from '../../lib/base-i18n';
import type { MemoDto } from '../../api/memos';

interface MemoListProps {
  /** '이 화면' 그룹(§4.9). 비어 있으면 헤더 자체를 렌더하지 않는다. */
  contextMemos: MemoDto[];
  contextTotal: number;
  /** 현재 화면명 — 그룹 헤더 문구. */
  contextLabel: string | null;
  /** '전체' 그룹 — 고정 포함(컨텍스트 그룹은 서버가 제외해 보낸다). */
  memos: MemoDto[];
  query: string;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  contextFirst: boolean;
  onQueryChange: (q: string) => void;
  onOpen: (memo: MemoDto) => void;
  onCreate: () => void;
  onTogglePin: (memo: MemoDto) => void;
  onDelete: (memo: MemoDto) => void;
  onExpandContext: () => void;
  onLoadMore: () => void;
  onToggleContextFirst: () => void;
  onGotoContext: (memo: MemoDto) => void;
  onRetry: () => void;
}

/** 제목이 없으면 본문 첫 줄로 대체(설계 §4.2 카드 규칙). */
function displayTitle(m: MemoDto): string {
  if (m.title && m.title.trim() !== '') return m.title;
  const firstLine = m.body.split('\n').find((l) => l.trim() !== '');
  return firstLine?.trim() || tMemo('memo.untitled');
}

/** 카드 2행 미리보기 — 제목으로 쓰인 첫 줄을 제외한 나머지. */
function preview(m: MemoDto): string {
  const lines = m.body.split('\n').map((l) => l.trim()).filter((l) => l !== '');
  const rest = (m.title && m.title.trim() !== '') ? lines : lines.slice(1);
  return rest.join(' ');
}

/** 상대 시각 — 7일 넘으면 절대 날짜(locale 은 base-i18n SSOT, bare toLocale* 금지 정책). */
function relTime(iso: string): string {
  const d = new Date(iso).getTime();
  if (!Number.isFinite(d)) return '';
  const diffMin = Math.floor((Date.now() - d) / 60000);
  if (diffMin < 1) return tMemo('memo.time.now');
  if (diffMin < 60) return tMemo('memo.time.min', { n: diffMin });
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return tMemo('memo.time.hour', { n: diffH });
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return tMemo('memo.time.yesterday');
  if (diffD < 7) return tMemo('memo.time.day', { n: diffD });
  return formatDate(iso);
}

function MemoCard({
  memo, onOpen, onTogglePin, onDelete, onGotoContext,
}: {
  memo: MemoDto;
  onOpen: (m: MemoDto) => void;
  onTogglePin: (m: MemoDto) => void;
  onDelete: (m: MemoDto) => void;
  onGotoContext: (m: MemoDto) => void;
}) {
  const p = preview(memo);
  return (
    <div className={`memo-card${memo.pinned ? ' memo-card--pinned' : ''}`}>
      <button type="button" className="memo-card-main" onClick={() => onOpen(memo)}>
        <span className="memo-card-title">{displayTitle(memo)}</span>
        {p !== '' && <span className="memo-card-preview">{p}</span>}
        <span className="memo-card-meta">
          <span className="memo-card-time">{relTime(memo.updatedAt)}</span>
        </span>
      </button>
      <span className="memo-card-side">
        <button
          type="button"
          className={`memo-card-act${memo.pinned ? ' is-on' : ''}`}
          onClick={() => onTogglePin(memo)}
          title={tMemo(memo.pinned ? 'memo.unpin' : 'memo.pin')}
          aria-label={tMemo(memo.pinned ? 'memo.unpin' : 'memo.pin')}
        >
          <Pin size={12} />
        </button>
        <button
          type="button"
          className="memo-card-act"
          onClick={() => onDelete(memo)}
          title={tMemo('memo.delete')}
          aria-label={tMemo('memo.delete')}
        >
          <Trash2 size={12} />
        </button>
      </span>
      {memo.contextLabel && (
        <button
          type="button"
          className="memo-card-badge"
          onClick={() => onGotoContext(memo)}
          title={tMemo('memo.goto_context', { name: memo.contextLabel })}
        >
          {memo.contextLabel}
        </button>
      )}
    </div>
  );
}

/**
 * MemoList — 목록 뷰 (design-20260724 §4.2 / §4.9).
 * 3그룹(고정 → 이 화면 → 전체)은 서버가 나눠 보낸 두 배열 + pinned 플래그로 구성한다.
 * 검색 중에는 그룹핑을 해제하고 결과만 보여준다(§4.9).
 */
export function MemoList(props: MemoListProps) {
  useSyncExternalStore(subscribeMemoLang, getMemoLang, getMemoLang);
  const {
    contextMemos, contextTotal, contextLabel, memos, query, loading, error,
    hasMore, contextFirst, onQueryChange, onOpen, onCreate, onTogglePin, onDelete,
    onExpandContext, onLoadMore, onToggleContextFirst, onGotoContext, onRetry,
  } = props;

  const searching = query.trim() !== '';
  const pinned = memos.filter((m) => m.pinned);
  const rest = memos.filter((m) => !m.pinned);
  const showContextGroup = !searching && contextFirst && contextMemos.length > 0;
  const isEmpty = !loading && memos.length === 0 && contextMemos.length === 0;

  const cardProps = { onOpen, onTogglePin, onDelete, onGotoContext };

  return (
    <>
      <div className="memo-search">
        <input
          className="memo-search-input"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={tMemo('memo.search_placeholder')}
          aria-label={tMemo('memo.search_placeholder')}
        />
        <button
          type="button"
          className={`memo-sort-toggle${contextFirst ? ' is-on' : ''}`}
          onClick={onToggleContextFirst}
          title={tMemo(contextFirst ? 'memo.context_first_on' : 'memo.context_first_off')}
          aria-label={tMemo(contextFirst ? 'memo.context_first_on' : 'memo.context_first_off')}
          aria-pressed={contextFirst}
        >
          <ArrowUpDown size={13} />
        </button>
      </div>

      <div className="memo-body scrollbar-thin">
        {error && (
          <div className="memo-error" role="alert">
            {error}
            <button type="button" onClick={onRetry}>{tMemo('memo.retry')}</button>
          </div>
        )}

        {isEmpty && (
          <div className="memo-empty">
            <span className="memo-empty-ico" aria-hidden />
            {searching ? (
              <span>{tMemo('memo.empty_search')}</span>
            ) : (
              <>
                <span>{tMemo('memo.empty_title')}<br />{tMemo('memo.empty_hint')}</span>
                <button type="button" className="memo-empty-cta" onClick={onCreate}>
                  <Plus size={13} /> {tMemo('memo.empty_cta')}
                </button>
              </>
            )}
          </div>
        )}

        {searching ? (
          memos.length > 0 && (
            <>
              <div className="memo-group">{tMemo('memo.group_search', { n: memos.length })}</div>
              {memos.map((m) => <MemoCard key={m.id} memo={m} {...cardProps} />)}
            </>
          )
        ) : (
          <>
            {pinned.length > 0 && (
              <>
                <div className="memo-group">{tMemo('memo.group_pinned')}</div>
                {pinned.map((m) => <MemoCard key={m.id} memo={m} {...cardProps} />)}
              </>
            )}

            {showContextGroup && (
              <>
                <div className="memo-group memo-group--context">
                  {tMemo('memo.group_context', { name: contextLabel ?? '' })}
                  <span className="memo-group-count">{contextTotal}</span>
                </div>
                {contextMemos.map((m) => <MemoCard key={m.id} memo={m} {...cardProps} />)}
                {contextTotal > contextMemos.length && (
                  <button type="button" className="memo-more" onClick={onExpandContext}>
                    {tMemo('memo.more_count', { n: contextTotal - contextMemos.length })}
                  </button>
                )}
              </>
            )}

            {rest.length > 0 && (
              <>
                {(pinned.length > 0 || showContextGroup) && (
                  <div className="memo-group">{tMemo('memo.group_all')}</div>
                )}
                {rest.map((m) => <MemoCard key={m.id} memo={m} {...cardProps} />)}
              </>
            )}
          </>
        )}

        {hasMore && (
          <button type="button" className="memo-more" onClick={onLoadMore} disabled={loading}>
            {tMemo('memo.load_more')}
          </button>
        )}
      </div>
    </>
  );
}
